import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { FaArrowRight, FaSlidersH, FaSearch } from "react-icons/fa";
import { searchPublishedModes } from "../../api/custom-mode";
import { isCommunityApiUnavailable, searchCommunity } from "../../api/community";
import PublishedModeCard from "../../components/cards/PublishedModeCard";
import { Button, Input, Modal, Spinner } from "../../ui";

const SORTS = [
  { value: "newest", label: "الأحدث" },
  { value: "oldest", label: "الأقدم" },
  { value: "mostLikes", label: "الأكثر إعجاباً" },
  { value: "leastLikes", label: "الأقل إعجاباً" },
];
const STEP_QUESTIONS = [0, 10, 50, 100, 300];
const STEP_LIKES = [0, 10, 50, 100, 500];
const STEP_PLAYED = [0, 20, 75, 200, 750];
const STEP_INVITED = [0, 10, 50, 100, 500];

const chipClass = (active) =>
  `h-9 px-3.5 rounded-full border text-sm font-semibold transition-colors ${
    active
      ? "bg-mainButton border-mainButton text-white"
      : "bg-white/[0.05] border-white/10 text-white/75 hover:text-white hover:border-white/25"
  }`;

const FilterRow = ({ label, options, value, onChange }) => (
  <div className="mb-5">
    <p className="text-sm font-semibold text-white/90 mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          aria-pressed={value === o}
          className={chipClass(value === o)}
        >
          {o === 0 ? "أي عدد" : `${o}+`}
        </button>
      ))}
    </div>
  </div>
);

const SearchModes = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: "newest",
    minQuestions: 0,
    minLikes: 0,
    minPlayedInGames: 0,
    minInvitedUsers: 0,
  });

  useEffect(() => {
    const t = setTimeout(() => setQ(input), 350);
    return () => clearTimeout(t);
  }, [input]);

  const filtersActive = Object.values(filters).some((v) => v && v !== "newest");
  // A plain text query uses the relevance-ranked, Arabic-aware search; the
  // numeric filters below belong to the previous endpoint, which is also the
  // fallback when the server doesn't have the new one.
  const ranked = q.trim().length >= 2 && !filtersActive;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["searchModes", q, filters, ranked],
      queryFn: async ({ pageParam }) => {
        if (ranked && !isCommunityApiUnavailable()) {
          try {
            const res = await searchCommunity({ q }, pageParam?.cursor);
            return {
              data: res.modes.data,
              total: res.modes.totalApprox,
              next: res.modes.hasMore ? { cursor: res.modes.nextCursor } : null,
            };
          } catch (error) {
            if (![404, 501].includes(error?.response?.status)) throw error;
          }
        }
        const res = await searchPublishedModes({ q, ...filters, page: pageParam?.page || 1 });
        return {
          data: res?.data || [],
          total: res?.totalCount,
          next: res?.page < res?.totalPages ? { page: res.page + 1 } : null,
        };
      },
      initialPageParam: null,
      getNextPageParam: (last) => last?.next || undefined,
      enabled: q.trim().length > 0 || filtersActive,
    });

  const modes = (data?.pages || []).flatMap((p) => p?.data || []);
  const total = data?.pages?.[0]?.total;
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => navigate("/app/community")}
          className="-mr-2 p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="رجوع"
        >
          <FaArrowRight />
        </button>
        <h1 className="text-2xl md:text-3xl font-extrabold">بحث</h1>
      </div>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setShowFilter(true)}
          className="relative shrink-0 w-12 h-12 rounded-xl border border-white/15 bg-white/[0.07] text-white hover:border-white/25 hover:bg-white/10 flex items-center justify-center transition-colors"
          aria-label="تصفية"
        >
          <FaSlidersH />
          {filtersActive && (
            <span className="absolute top-2 left-2 w-2 h-2 rounded-full bg-mainButton" aria-hidden="true" />
          )}
        </button>
        <div className="relative flex-1">
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="ابحث بإسم الفئة أو المستخدم..."
            aria-label="بحث"
            className="pr-11"
          />
          <FaSearch
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
            aria-hidden="true"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-white/80">
          <Spinner size={28} />
        </div>
      ) : modes.length === 0 ? (
        <div className="text-center py-16">
          <FaSearch className="mx-auto text-4xl text-white/15 mb-4" aria-hidden="true" />
          <p className="text-white/55">{q ? "لا توجد نتائج" : "ابحث عن فئة"}</p>
        </div>
      ) : (
        <>
          {typeof total === "number" && (
            <p className="text-white/55 text-sm mb-3">{total} نتيجة</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {modes.map((m) => (
              <PublishedModeCard key={m._id} mode={m} />
            ))}
          </div>
          {hasNextPage && (
            <div className="flex justify-center mt-8">
              <Button
                variant="secondary"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                icon={isFetchingNextPage ? <Spinner /> : null}
              >
                عرض المزيد
              </Button>
            </div>
          )}
        </>
      )}

      <Modal open={showFilter} onClose={() => setShowFilter(false)} title="تصفية النتائج">
        <p className="text-sm font-semibold text-white/90 mb-2">الترتيب</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {SORTS.map((s) => (
            <button
              key={s.value}
              onClick={() => setFilter("sortBy", s.value)}
              aria-pressed={filters.sortBy === s.value}
              className={chipClass(filters.sortBy === s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <FilterRow
          label="أقل عدد أسئلة"
          options={STEP_QUESTIONS}
          value={filters.minQuestions}
          onChange={(v) => setFilter("minQuestions", v)}
        />
        <FilterRow
          label="أقل عدد إعجابات"
          options={STEP_LIKES}
          value={filters.minLikes}
          onChange={(v) => setFilter("minLikes", v)}
        />
        <FilterRow
          label="أقل مرات لعب"
          options={STEP_PLAYED}
          value={filters.minPlayedInGames}
          onChange={(v) => setFilter("minPlayedInGames", v)}
        />
        <FilterRow
          label="أقل عدد مضيفين"
          options={STEP_INVITED}
          value={filters.minInvitedUsers}
          onChange={(v) => setFilter("minInvitedUsers", v)}
        />

        <div className="flex gap-3 mt-6">
          <Button className="flex-1" onClick={() => setShowFilter(false)}>
            تطبيق
          </Button>
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() =>
              setFilters({
                sortBy: "newest",
                minQuestions: 0,
                minLikes: 0,
                minPlayedInGames: 0,
                minInvitedUsers: 0,
              })
            }
          >
            إعادة ضبط
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SearchModes;
